import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { formatApiError } from "@/lib/api";
import { COLOR_FIELDS, RADII, UI_FONTS } from "@/lib/theme";
import { useAppearance } from "@/context/AppearanceContext";

const HEX = /^#[0-9a-fA-F]{6}$/;
const sel = "h-10 w-full rounded-md bg-inset border border-line text-sm text-slate-200 px-3";

const ColorField = ({ k, label, value, onChange }) => {
  const [txt, setTxt] = useState(value);
  useEffect(() => setTxt(value), [value]);
  return (
    <label className="flex items-center gap-3 rounded-xl border border-line bg-inset px-3 py-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-8 shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-0" data-testid={`theme-color-${k}`} />
      <span className="flex-1 text-sm text-slate-300">{label}</span>
      <input value={txt} onChange={(e) => { setTxt(e.target.value); if (HEX.test(e.target.value)) onChange(e.target.value.toLowerCase()); }}
        className="w-20 bg-transparent font-code text-xs text-slate-400 outline-none" maxLength={7} data-testid={`theme-hex-${k}`} />
    </label>
  );
};

export const ThemeEditor = ({ initial, onDone }) => {
  const { saveTheme, setTheme, setPreview } = useAppearance();
  const [name, setName] = useState(initial.name);
  const [v, setV] = useState(initial.values);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPreview(v); }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setPreview(null), []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (val) => setV((x) => ({ ...x, [k]: val }));
  const save = async () => {
    if (!name.trim()) return toast.error("Give your theme a name");
    setBusy(true);
    try { const t = await saveTheme(initial.id, name.trim(), v); await setTheme(t.id); toast.success(`"${t.name}" saved and applied`); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-500/40 bg-panel p-5 sm:p-6 space-y-5 rise" data-testid="theme-editor">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[12rem] space-y-1.5 text-sm text-slate-200"><span>Theme name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="theme-name-input" /></label>
        <p className="text-xs text-slate-500 pb-2">Changes preview across the app as you go.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {COLOR_FIELDS.map(([k, l]) => <ColorField key={k} k={k} label={l} value={v[k]} onChange={set(k)} />)}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="space-y-1.5 text-sm text-slate-200"><span>Heading font</span>
          <select value={v.heading_font} onChange={(e) => set("heading_font")(e.target.value)} className={sel} style={{ fontFamily: v.heading_font }} data-testid="theme-heading-font">
            {UI_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}</select></label>
        <label className="space-y-1.5 text-sm text-slate-200"><span>Body font</span>
          <select value={v.body_font} onChange={(e) => set("body_font")(e.target.value)} className={sel} style={{ fontFamily: v.body_font }} data-testid="theme-body-font">
            {UI_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}</select></label>
        <div className="space-y-1.5 text-sm text-slate-200"><span>Corners</span>
          <div className="flex rounded-full border border-line p-0.5" data-testid="theme-radius">
            {RADII.map(([k, l]) => (
              <button key={k} type="button" onClick={() => set("radius")(k)} aria-pressed={v.radius === k}
                className={`flex-1 rounded-full py-1.5 text-xs transition-colors ${v.radius === k ? "bg-amber-500 text-onaccent font-semibold" : "text-slate-400 hover:text-slate-200"}`} data-testid={`theme-radius-${k}`}>{l}</button>
            ))}
          </div></div>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-line bg-inset px-3 h-10 self-end text-sm text-slate-300">
          Paper texture <Switch checked={v.grain} onCheckedChange={set("grain")} data-testid="theme-grain-switch" /></label>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-onaccent text-sm font-semibold px-6 h-10" data-testid="theme-save-btn">
          {busy ? "Saving…" : initial.id ? "Save changes" : "Save theme"}</button>
        <button onClick={onDone} className="rounded-full border border-line px-5 h-10 text-sm text-slate-300 hover:border-amber-500/40" data-testid="theme-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};
